import React, { useState, useEffect, useCallback } from 'react';
import { useData } from '../contexts/DataContext';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import {
  Plus, Edit, Trash2, Download, Upload, Search,
  Forklift, ChevronDown, ChevronUp, Filter, MoreHorizontal,
  Eye, Clock, Building2, MapPin, QrCode, Printer
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator
} from '../components/ui/dropdown-menu';
import AuditLogDialog from '../components/AuditLogDialog';

// Equipment types & brands in UPPERCASE for matching
const EQUIPMENT_TYPES = [
  "Liza electrica", "Electrostivuitor", "Transpaleta electrica",
  "Transpaleta cu catarg", "Motostivuitor", "Motostivuitor gaz",
  "Macara pod rulant", "Masina de ridicat de tip special",
  "Platforma nedeplasabila", "Platforma deplasabila",
  "Recipient stabil", "Nacela", "Electropalan", "Altele",
];
const BRAND_LIST = [
  "STILL", "CROWN", "HYUNDAI", "GUTMANN", "COMBILIFT",
  "HANGCHA", "LINDE AG", "KONECRANES", "JEKKO", "ERGOLIFT",
  "OKS OTTO", "ALTELE",
];

const ISCIREquipmentPage = () => {
  const { plants, locations } = useData();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [departments, setDepartments] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [showDetail, setShowDetail] = useState(null);
  const [search, setSearch] = useState('');
  const [filterDept, setFilterDept] = useState('all');
  const [filterBrand, setFilterBrand] = useState('all');
  const [sortField, setSortField] = useState('nr_intern');
  const [sortDir, setSortDir] = useState('asc');
  const [importLoading, setImportLoading] = useState(false);
  const [showAuditLog, setShowAuditLog] = useState(null);
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [qrLoading, setQrLoading] = useState(false);
  const [showQRPreview, setShowQRPreview] = useState(null);
  const [qrPreviewData, setQrPreviewData] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [qrFields, setQrFields] = useState({
    code: true,
    denumire: true,
    numar_inventar: true,
    marca: true,
    serie_utilaj: true,
    departament: true,
    service_baterie: true,
    responsabil: false,
    capacitate: false,
  });

  const emptyForm = {
    denumire: '', marca: '', serie_utilaj: '',
    numar_inventar: '',
    cod_alocare_intern: '',
    plant_id: '', plant_name: '', location_id: '', location_name: '',
    departament: '', responsabil: '',
    scadenta_iscir: '', are_iscir: true,
    defecte_observatii: '', capacitate: '', an_fabricatie: '', notes: '',
    iscir_category: '',
    necesita_service_baterie: false, tip_baterie: '', interval_service_zile: 30
  };
  const [form, setForm] = useState(emptyForm);
  const [iscirCategories, setIscirCategories] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [rRes, sRes, dRes, cRes] = await Promise.all([
        api.get('/iscir-equipment'),
        api.get('/iscir-equipment/stats'),
        api.get('/iscir-equipment/departments'),
        api.get('/iscir/categories'),
      ]);
      setRecords(rRes.data);
      setStats(sRes.data);
      setDepartments(dRes.data);
      setIscirCategories(cRes.data || []);
    } catch (err) {
      toast.error('Eroare la incarcarea datelor');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async () => {
    if (!form.denumire) {
      toast.error('Completati denumirea echipamentului');
      return;
    }
    try {
      const payload = { ...form, scadenta_iscir: form.scadenta_iscir || null };
      if (editItem) {
        await api.put(`/iscir-equipment/${editItem.id}`, payload);
        toast.success('Echipament ISCIR actualizat!');
      } else {
        await api.post('/iscir-equipment', payload);
        toast.success('Echipament ISCIR adaugat!');
      }
      setShowDialog(false);
      setEditItem(null);
      setForm(emptyForm);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la salvare');
    }
  };

  const handleEdit = (item) => {
    setEditItem(item);
    // Match the denumire/marca to the closest option in the lists
    const matchDenumire = EQUIPMENT_TYPES.find(t => t.toUpperCase() === (item.denumire || '').toUpperCase()) || item.denumire || '';
    const matchMarca = BRAND_LIST.find(b => b.toUpperCase() === (item.marca || '').toUpperCase()) || item.marca || '';

    setForm({
      denumire: matchDenumire,
      marca: matchMarca,
      serie_utilaj: item.serie_utilaj || '',
      numar_inventar: item.numar_inventar || '',
      cod_alocare_intern: item.cod_alocare_intern || '',
      plant_id: item.plant_id || '',
      plant_name: item.plant_name || '',
      location_id: item.location_id || '',
      location_name: item.location_name || '',
      departament: item.departament || '',
      responsabil: item.responsabil || '',
      scadenta_iscir: item.scadenta_iscir || '',
      are_iscir: item.are_iscir !== false,
      defecte_observatii: item.defecte_observatii || '',
      capacitate: item.capacitate || '',
      an_fabricatie: item.an_fabricatie || '',
      notes: item.notes || '',
      iscir_category: item.iscir_category || '',
      necesita_service_baterie: item.necesita_service_baterie || false,
      tip_baterie: item.tip_baterie || '',
      interval_service_zile: item.interval_service_zile || 30,
    });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sigur doriti sa stergeti acest echipament ISCIR?')) return;
    try {
      await api.delete(`/iscir-equipment/${id}`);
      toast.success('Echipament ISCIR sters');
      fetchData();
    } catch (err) { toast.error('Eroare la stergere'); }
  };

  const handleExport = async () => {
    try {
      const res = await api.get('/iscir-equipment/export/xlsx', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `nomenclator_iscir_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export Excel descarcat');
    } catch (err) { toast.error('Eroare la export'); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/iscir-equipment/import/xlsx', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`${res.data.message}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la import');
    } finally {
      setImportLoading(false);
      e.target.value = '';
    }
  };

  const qrFieldOptions = [
    { key: 'code', label: 'Cod ISCIR' },
    { key: 'denumire', label: 'Denumire' },
    { key: 'numar_inventar', label: 'Nr. Inventar' },
    { key: 'marca', label: 'Marca' },
    { key: 'serie_utilaj', label: 'Serie utilaj' },
    { key: 'departament', label: 'Departament' },
    { key: 'service_baterie', label: 'Service Baterie' },
    { key: 'responsabil', label: 'Responsabil' },
    { key: 'capacitate', label: 'Capacitate' },
  ];

  const handleGenerateQR = async (format) => {
    setQrLoading(true);
    try {
      const fieldsStr = Object.keys(qrFields).filter(k => qrFields[k]).join(',');
      const ids = selectedIds.size > 0 ? [...selectedIds].join(',') : '';
      let url = `/iscir-equipment/export/qr-codes?format=${format}&fields=${fieldsStr}`;
      if (ids) url += `&equipment_ids=${ids}`;
      const res = await api.get(url, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = blobUrl; a.download = `qr_echipamente_iscir_${format}.pdf`; a.click();
      URL.revokeObjectURL(blobUrl);
      setShowQRDialog(false);
      const count = selectedIds.size > 0 ? selectedIds.size : records.length;
      toast.success(`Export QR (${format}) pentru ${count} echipamente generat!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la generare QR');
    } finally { setQrLoading(false); }
  };

  const handleExportQrZip = async () => {
    setQrLoading(true);
    try {
      const fieldsStr = Object.keys(qrFields).filter(k => qrFields[k]).join(',');
      const ids = selectedIds.size > 0 ? [...selectedIds].join(',') : '';
      let url = `/iscir-equipment/export/qr-zip?fields=${fieldsStr}`;
      if (ids) url += `&equipment_ids=${ids}`;
      const res = await api.get(url, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = blobUrl; a.download = `qr_echipamente_iscir.zip`; a.click();
      URL.revokeObjectURL(blobUrl);
      setShowQRDialog(false);
      const count = selectedIds.size > 0 ? selectedIds.size : records.length;
      toast.success(`Arhiva ZIP cu ${count} imagini QR generata!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la export ZIP');
    } finally { setQrLoading(false); }
  };

  const handlePreviewQR = async (item) => {
    setShowQRPreview(item);
    setQrPreviewData(null);
    try {
      const res = await api.get(`/iscir-equipment/qr-preview/${item.id}`);
      setQrPreviewData(res.data);
    } catch { toast.error('Eroare la generare previzualizare QR'); }
  };

  const handlePlantChange = (plantId) => {
    const plant = plants.find(p => p.id === plantId);
    setForm({ ...form, plant_id: plantId, plant_name: plant?.name || '', location_id: '', location_name: '' });
  };

  const handleLocationChange = (locId) => {
    const loc = filteredLocations.find(l => l.id === locId);
    setForm({ ...form, location_id: locId, location_name: loc?.name || '' });
  };

  const filteredLocations = locations.filter(l => !form.plant_id || l.plant_id === form.plant_id);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronDown className="w-3 h-3 text-slate-300" />;
    return sortDir === 'asc' ? <ChevronUp className="w-3 h-3 text-blue-600" /> : <ChevronDown className="w-3 h-3 text-blue-600" />;
  };

  const filtered = records
    .filter(r => {
      if (filterDept !== 'all' && r.departament !== filterDept) return false;
      if (filterBrand !== 'all' && r.marca !== filterBrand) return false;
      if (search) {
        const s = search.toLowerCase();
        return (r.denumire || '').toLowerCase().includes(s) ||
          (r.code || '').toLowerCase().includes(s) ||
          (r.marca || '').toLowerCase().includes(s) ||
          (r.serie_utilaj || '').toLowerCase().includes(s) ||
          (r.departament || '').toLowerCase().includes(s) ||
          (r.responsabil || '').toLowerCase().includes(s) ||
          (r.cod_alocare_intern || '').toLowerCase().includes(s) ||
          (r.numar_inventar || '').toLowerCase().includes(s) ||
          String(r.nr_intern || '').includes(s);
      }
      return true;
    })
    .sort((a, b) => {
      let va = a[sortField], vb = b[sortField];
      if (va == null) va = '';
      if (vb == null) vb = '';
      if (typeof va === 'number' && typeof vb === 'number') return sortDir === 'asc' ? va - vb : vb - va;
      return sortDir === 'asc' ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });

  const uniqueBrands = [...new Set(records.map(r => r.marca).filter(Boolean))].sort();

  return (
    <div className="space-y-5" data-testid="iscir-equipment-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl flex items-center justify-center shadow-lg shadow-blue-600/20">
            <Forklift className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight" data-testid="page-title">Nomenclator Echipamente ISCIR</h1>
            <p className="text-sm text-slate-500 mt-0.5">{records.length} echipamente inregistrate</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => setShowQRDialog(true)} data-testid="qr-generate-btn">
            <QrCode className="w-4 h-4 mr-1.5" /> Export QR
            {selectedIds.size > 0 && <Badge className="ml-1.5 bg-blue-600 text-white text-[10px] px-1.5 py-0 h-5">{selectedIds.size}</Badge>}
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport} data-testid="export-btn"><Download className="w-4 h-4 mr-1.5" /> Export Excel</Button>
          <div className="relative">
            <input type="file" accept=".xlsx,.xls" onChange={handleImport} className="absolute inset-0 opacity-0 w-full cursor-pointer" disabled={importLoading} data-testid="import-input" />
            <Button variant="outline" size="sm" disabled={importLoading} data-testid="import-btn"><Upload className="w-4 h-4 mr-1.5" /> {importLoading ? 'Se importa...' : 'Import Excel'}</Button>
          </div>
          <Button className="bg-[#0052CC] hover:bg-[#0052CC]/90 text-white" size="sm" onClick={() => { setEditItem(null); setForm(emptyForm); setShowDialog(true); }} data-testid="add-equipment-btn"><Plus className="w-4 h-4 mr-1.5" /> Adauga Echipament</Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total echipamente', value: stats.total || 0, color: 'text-slate-900', border: '' },
          { label: 'Operationale', value: stats.operational || 0, color: 'text-emerald-700', border: 'border-l-4 border-l-emerald-500' },
          { label: 'Defecte / Casate', value: stats.defect || 0, color: 'text-red-700', border: 'border-l-4 border-l-red-500' },
          { label: 'Fara ISCIR', value: stats.fara_iscir || 0, color: 'text-slate-600', border: 'border-l-4 border-l-slate-400' },
        ].map((s, i) => (
          <Card key={i} className={`border shadow-sm ${s.border}`}><CardContent className="p-4 text-center">
            <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            <p className="text-xs text-slate-500 uppercase tracking-wider">{s.label}</p>
          </CardContent></Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Cauta denumire, cod, serie, marca, departament..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9 text-sm" data-testid="search-input" />
        </div>
        <Select value={filterDept} onValueChange={setFilterDept}>
          <SelectTrigger className="w-44 h-9 text-sm" data-testid="filter-dept"><Filter className="w-3.5 h-3.5 mr-1" /><SelectValue placeholder="Departament" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Toate departamentele</SelectItem>{departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filterBrand} onValueChange={setFilterBrand}>
          <SelectTrigger className="w-36 h-9 text-sm" data-testid="filter-brand"><SelectValue placeholder="Marca" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Toate marcile</SelectItem>{uniqueBrands.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
        </Select>
        {(filterDept !== 'all' || filterBrand !== 'all' || search) && (
          <Button variant="ghost" size="sm" className="text-xs text-slate-500" onClick={() => { setFilterDept('all'); setFilterBrand('all'); setSearch(''); }}>Reseteaza filtre</Button>
        )}
      </div>

      {/* Selection bar */}
      {selectedIds.size > 0 && (
        <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-4 py-2" data-testid="selection-bar">
          <span className="text-sm text-blue-700"><strong>{selectedIds.size}</strong> echipamente selectate</span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-7 text-xs border-blue-300 text-blue-700 hover:bg-blue-100" onClick={() => setShowQRDialog(true)} data-testid="selection-export-qr">
              <QrCode className="w-3.5 h-3.5 mr-1" /> Export QR selectie
            </Button>
            <Button variant="ghost" size="sm" className="h-7 text-xs text-blue-500" onClick={() => setSelectedIds(new Set())} data-testid="deselect-all-btn">Deselecteaza tot</Button>
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0052CC]" /></div>
      ) : (
        <Card className="border shadow-sm">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="equipment-table">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="p-2 w-10">
                      <Checkbox
                        checked={filtered.length > 0 && filtered.every(r => selectedIds.has(r.id))}
                        onCheckedChange={(checked) => {
                          if (checked) setSelectedIds(new Set(filtered.map(r => r.id)));
                          else setSelectedIds(new Set());
                        }}
                        data-testid="select-all-checkbox"
                      />
                    </th>
                    {[
                      { key: 'nr_intern', label: 'COD INTERN', w: 'w-20' },
                      { key: 'cod_alocare_intern', label: 'COD ALOCARE', w: 'w-24' },
                      { key: 'numar_inventar', label: 'Nr. Inventar', w: 'w-28' },
                      { key: 'code', label: 'Cod ISCIR', w: 'w-28' },
                      { key: 'denumire', label: 'Denumire', w: 'min-w-[170px]' },
                      { key: 'iscir_category', label: 'Categorie ISCIR', w: 'w-32' },
                      { key: 'marca', label: 'Marca', w: 'w-24' },
                      { key: 'serie_utilaj', label: 'Serie utilaj', w: 'min-w-[160px]' },
                      { key: 'plant_name', label: 'Sediu', w: 'w-32' },
                      { key: 'location_name', label: 'Locatie', w: 'w-32' },
                      { key: 'departament', label: 'Departament', w: 'w-36' },
                      { key: 'responsabil', label: 'Responsabil', w: 'w-32' },
                      { key: 'defecte_observatii', label: 'Defecte / Obs.', w: 'min-w-[160px]' },
                    ].map(col => (
                      <th key={col.key} className={`p-2 text-left font-semibold text-slate-700 ${col.w} cursor-pointer select-none hover:bg-slate-100 transition-colors`} onClick={() => toggleSort(col.key)}>
                        <div className="flex items-center gap-1"><span className="text-[11px] uppercase tracking-wider">{col.label}</span><SortIcon field={col.key} /></div>
                      </th>
                    ))}
                    <th className="p-2 text-right font-semibold text-slate-700 w-14 text-[11px] uppercase">Actiuni</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.length === 0 && (
                    <tr><td colSpan={15} className="p-16 text-center text-slate-400">
                      <Forklift className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p className="font-medium">Niciun echipament ISCIR gasit</p>
                    </td></tr>
                  )}
                  {filtered.map(r => (
                    <tr key={r.id} className={`hover:bg-blue-50/40 transition-colors ${selectedIds.has(r.id) ? 'bg-blue-50/60' : ''}`} data-testid={`equipment-row-${r.code}`}>
                      <td className="p-2">
                        <Checkbox
                          checked={selectedIds.has(r.id)}
                          onCheckedChange={(checked) => {
                            setSelectedIds(prev => {
                              const next = new Set(prev);
                              if (checked) next.add(r.id);
                              else next.delete(r.id);
                              return next;
                            });
                          }}
                          data-testid={`select-${r.code}`}
                        />
                      </td>
                      <td className="p-2"><span className="font-mono text-sm font-bold text-slate-900 bg-slate-100 px-2 py-0.5 rounded">{r.nr_intern || '-'}</span></td>
                      <td className="p-2 text-xs font-mono text-slate-700">{r.cod_alocare_intern || '-'}</td>
                      <td className="p-2">{r.numar_inventar ? <span className="font-mono text-xs font-semibold text-indigo-700 bg-indigo-50 px-1.5 py-0.5 rounded">{r.numar_inventar}</span> : <span className="text-xs text-slate-400">-</span>}</td>
                      <td className="p-2"><span className="font-mono text-xs font-medium text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded">{r.code}</span></td>
                      <td className="p-2 font-medium text-slate-900 text-xs">{r.denumire}</td>
                      <td className="p-2 text-xs text-slate-600">{r.iscir_category || '-'}</td>
                      <td className="p-2 text-xs text-slate-700 font-medium">{r.marca}</td>
                      <td className="p-2 font-mono text-xs text-slate-600">{r.serie_utilaj}</td>
                      <td className="p-2 text-xs text-slate-600">{r.plant_name || '-'}</td>
                      <td className="p-2 text-xs text-slate-600">{r.location_name || '-'}</td>
                      <td className="p-2 text-xs text-slate-600">{r.departament || '-'}</td>
                      <td className="p-2 text-xs text-slate-600">{r.responsabil || '-'}</td>
                      <td className="p-2 text-xs text-slate-500 max-w-[200px] truncate" title={r.defecte_observatii || ''}>{r.defecte_observatii || '-'}</td>
                      <td className="p-2">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild><Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`actions-${r.code}`}><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem onClick={() => setShowDetail(r)}><Eye className="w-4 h-4 mr-2" /> Detalii</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleEdit(r)}><Edit className="w-4 h-4 mr-2" /> Editeaza</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handlePreviewQR(r)}><QrCode className="w-4 h-4 mr-2" /> Cod QR</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setShowAuditLog(r)}><Clock className="w-4 h-4 mr-2" /> Istoric modificari</DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleDelete(r.id)} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" /> Sterge</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-2 border-t bg-slate-50 text-xs text-slate-500">Afisate: {filtered.length} din {records.length}</div>
          </CardContent>
        </Card>
      )}

      {/* Detail Dialog */}
      <Dialog open={!!showDetail} onOpenChange={o => { if (!o) setShowDetail(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {showDetail && (<>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <Forklift className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="text-lg">{showDetail.denumire}</p>
                  <p className="text-sm font-mono text-blue-600 font-normal">{showDetail.code}</p>
                </div>
              </DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              {[
                ['Cod intern (Nr.)', showDetail.nr_intern || '-'],
                ['Cod alocare intern', showDetail.cod_alocare_intern || '-'],
                ['Nr. Inventar', showDetail.numar_inventar || '-'],
                ['Cod ISCIR', showDetail.code],
                ['Categorie ISCIR', showDetail.iscir_category || '-'],
                ['Marca', showDetail.marca],
                ['Serie utilaj', showDetail.serie_utilaj],
                ['Sediu', showDetail.plant_name || '-'],
                ['Locatie', showDetail.location_name || '-'],
                ['Departament', showDetail.departament],
                ['Responsabil', showDetail.responsabil],
                ['Capacitate', showDetail.capacitate || '-'],
                ['Necesita ISCIR', showDetail.are_iscir ? 'DA' : 'NU'],
              ].map(([label, val], i) => (
                <div key={i} className="bg-slate-50 rounded-lg p-3">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider">{label}</p>
                  <p className="text-sm font-medium text-slate-800 mt-0.5">{val || '-'}</p>
                </div>
              ))}
            </div>
            {showDetail.defecte_observatii && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3">
                <p className="text-[10px] text-amber-600 uppercase tracking-wider font-medium">Defecte / Observatii</p>
                <p className="text-sm text-amber-900 mt-1">{showDetail.defecte_observatii}</p>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowDetail(null); handleEdit(showDetail); }}><Edit className="w-4 h-4 mr-2" /> Editeaza</Button>
              <Button variant="outline" onClick={() => setShowDetail(null)}>Inchide</Button>
            </DialogFooter>
          </>)}
        </DialogContent>
      </Dialog>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={o => { setShowDialog(o); if (!o) { setEditItem(null); setForm(emptyForm); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Forklift className="w-5 h-5 text-blue-600" />
              {editItem ? 'Editeaza Echipament ISCIR' : 'Adauga Echipament ISCIR'}
              {editItem && <Badge variant="secondary" className="font-mono text-xs ml-2">{editItem.code}</Badge>}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5">
            {/* Identificare */}
            <div>
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <div className="w-1 h-4 bg-blue-600 rounded-full" /> Identificare
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Denumire echipament *</Label>
                  <Select value={form.denumire} onValueChange={v => setForm({ ...form, denumire: v })}>
                    <SelectTrigger className="h-10" data-testid="form-denumire"><SelectValue placeholder="Selecteaza tipul" /></SelectTrigger>
                    <SelectContent>
                      {EQUIPMENT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Marca</Label>
                  <Select value={form.marca} onValueChange={v => setForm({ ...form, marca: v })}>
                    <SelectTrigger className="h-10" data-testid="form-marca"><SelectValue placeholder="Selecteaza marca" /></SelectTrigger>
                    <SelectContent>
                      {BRAND_LIST.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Serie utilaj</Label>
                  <Input value={form.serie_utilaj} onChange={e => setForm({ ...form, serie_utilaj: e.target.value })} className="h-10 font-mono" placeholder="Nr. serie fabricant" data-testid="form-serie" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Capacitate</Label>
                  <Input value={form.capacitate} onChange={e => setForm({ ...form, capacitate: e.target.value })} className="h-10" placeholder="Ex: 2.5T, 5000L" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Categorie ISCIR</Label>
                  <Select value={form.iscir_category} onValueChange={v => setForm({ ...form, iscir_category: v })}>
                    <SelectTrigger className="h-10" data-testid="form-iscir-category"><SelectValue placeholder="Selecteaza categoria" /></SelectTrigger>
                    <SelectContent>
                      {iscirCategories.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">An fabricatie</Label>
                  <Input value={form.an_fabricatie} onChange={e => setForm({ ...form, an_fabricatie: e.target.value })} className="h-10" placeholder="Ex: 2020" />
                </div>
                <div className="space-y-1.5 col-span-2">
                  <Label className="text-xs font-semibold text-blue-700">Cod alocare intern</Label>
                  <Input value={form.cod_alocare_intern} onChange={e => setForm({ ...form, cod_alocare_intern: e.target.value })} className="h-10 border-blue-200 focus:border-blue-400" placeholder="Introduceti codul de alocare intern" data-testid="form-cod-alocare" />
                </div>
                <div className="space-y-1.5 col-span-2">
                  <Label className="text-xs font-semibold text-indigo-700">Nr. Inventar</Label>
                  <Input value={form.numar_inventar} onChange={e => setForm({ ...form, numar_inventar: e.target.value })} className="h-10 border-indigo-200 focus:border-indigo-400" placeholder="ex: INV-00123" data-testid="form-numar-inventar" />
                </div>
              </div>
            </div>

            {/* Localizare */}
            <div>
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <div className="w-1 h-4 bg-emerald-600 rounded-full" /> Localizare & Responsabil
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs flex items-center gap-1"><Building2 className="w-3 h-3" /> Sediu</Label>
                  <Select value={form.plant_id || '__none'} onValueChange={v => handlePlantChange(v === '__none' ? '' : v)}>
                    <SelectTrigger className="h-10" data-testid="form-plant"><SelectValue placeholder="Selecteaza sediul" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">-- Fara sediu --</SelectItem>
                      {plants?.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs flex items-center gap-1"><MapPin className="w-3 h-3" /> Locatie</Label>
                  <Select value={form.location_id || '__none'} onValueChange={v => handleLocationChange(v === '__none' ? '' : v)}>
                    <SelectTrigger className="h-10" data-testid="form-location"><SelectValue placeholder="Selecteaza locatia" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">-- Fara locatie --</SelectItem>
                      {filteredLocations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Departament</Label>
                  <Input value={form.departament} onChange={e => setForm({ ...form, departament: e.target.value })} className="h-10" placeholder="Ex: GESTIUNE, LIVRARI EXPORT" data-testid="form-dept" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Responsabil departament</Label>
                  <Input value={form.responsabil} onChange={e => setForm({ ...form, responsabil: e.target.value })} className="h-10" placeholder="Nume responsabil" data-testid="form-resp" />
                </div>
              </div>
            </div>

            {/* ISCIR */}
            <div>
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <div className="w-1 h-4 bg-amber-600 rounded-full" /> Conformitate ISCIR
              </h3>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={form.are_iscir} onChange={e => setForm({ ...form, are_iscir: e.target.checked })} className="w-4 h-4 rounded border-slate-300" />
                <span className="text-sm">Echipamentul necesita verificare ISCIR</span>
              </label>
            </div>

            {/* Service Baterie */}
            <div>
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <div className="w-1 h-4 bg-cyan-600 rounded-full" /> Service Baterie
              </h3>
              <label className="flex items-center gap-3 cursor-pointer mb-3">
                <input type="checkbox" checked={form.necesita_service_baterie} onChange={e => setForm({ ...form, necesita_service_baterie: e.target.checked })} className="w-4 h-4 rounded border-slate-300" data-testid="form-necesita-baterie" />
                <span className="text-sm">Necesita completare lichid baterie</span>
              </label>
              {form.necesita_service_baterie && (
                <div className="grid grid-cols-2 gap-4 mt-2 pl-7">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Tip baterie</Label>
                    <select value={form.tip_baterie} onChange={e => setForm({ ...form, tip_baterie: e.target.value })} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="form-tip-baterie">
                      <option value="">Selecteaza...</option>
                      <option value="CU INTRETINERE">Cu intretinere</option>
                      <option value="FARA INTRETINERE">Fara intretinere</option>
                      <option value="GEL">Gel</option>
                      <option value="LITIU">Litiu-ion</option>
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Interval service (zile)</Label>
                    <Input type="number" min={1} max={365} value={form.interval_service_zile} onChange={e => setForm({ ...form, interval_service_zile: parseInt(e.target.value) || 30 })} className="h-10" data-testid="form-interval-baterie" />
                  </div>
                </div>
              )}
            </div>

            {/* Defecte */}
            <div>
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <div className="w-1 h-4 bg-red-600 rounded-full" /> Defecte & Observatii
              </h3>
              <Textarea value={form.defecte_observatii} onChange={e => setForm({ ...form, defecte_observatii: e.target.value })} rows={2} placeholder="Defecte constatate, revizii efectuate..." className="text-sm" data-testid="form-defecte" />
              <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Note suplimentare..." className="text-sm mt-2" />
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowDialog(false)}>Anuleaza</Button>
            <Button className="bg-[#0052CC] hover:bg-[#0052CC]/90 text-white" onClick={handleSubmit} data-testid="save-btn">
              {editItem ? 'Actualizeaza' : 'Adauga echipament'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit Log */}
      {showAuditLog && (
        <AuditLogDialog open={!!showAuditLog} onClose={() => setShowAuditLog(null)} entityType="iscir_equipment" entityId={showAuditLog.id} entityName={`${showAuditLog.code} - ${showAuditLog.denumire}`} />
      )}

      {/* QR Export Dialog */}
      <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
        <DialogContent className="max-w-md" data-testid="qr-generate-dialog">
          <DialogHeader>
            <DialogTitle>Export Coduri QR</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-500">
              Se vor exporta {selectedIds.size > 0 ? selectedIds.size : records.length} echipamente
              {selectedIds.size > 0 && <span className="text-blue-600 font-medium"> (selectie)</span>}
            </p>

            {/* Field Selection */}
            <div className="space-y-3">
              <p className="text-sm font-medium text-slate-700">Campuri afisate pe eticheta:</p>
              <div className="grid grid-cols-2 gap-2">
                {qrFieldOptions.map(field => (
                  <div key={field.key} className="flex items-center space-x-2">
                    <Checkbox
                      id={`qr-field-${field.key}`}
                      checked={qrFields[field.key]}
                      onCheckedChange={(checked) => setQrFields({ ...qrFields, [field.key]: checked })}
                      data-testid={`qr-field-${field.key}`}
                    />
                    <label htmlFor={`qr-field-${field.key}`} className="text-sm cursor-pointer">{field.label}</label>
                  </div>
                ))}
              </div>
            </div>

            <Separator />

            <div className="grid gap-3">
              <Button variant="outline" className="h-auto py-4 justify-start" onClick={() => handleGenerateQR('standard')} disabled={qrLoading} data-testid="qr-format-standard">
                <div className="text-left">
                  <p className="font-semibold">Standard (24/pagina)</p>
                  <p className="text-xs text-slate-500">3 coloane x 8 randuri - ideal pentru A4</p>
                </div>
              </Button>
              <Button variant="outline" className="h-auto py-4 justify-start" onClick={() => handleGenerateQR('large')} disabled={qrLoading} data-testid="qr-format-large">
                <div className="text-left">
                  <p className="font-semibold">Mari (8/pagina)</p>
                  <p className="text-xs text-slate-500">2 coloane x 4 randuri - etichete mari vizibile</p>
                </div>
              </Button>
              <Button variant="outline" className="h-auto py-4 justify-start" onClick={() => handleGenerateQR('industrial')} disabled={qrLoading} data-testid="qr-format-industrial">
                <div className="text-left">
                  <p className="font-semibold">Industrial (16/pagina)</p>
                  <p className="text-xs text-slate-500">2 coloane x 8 randuri - 105x38mm etichete mici</p>
                </div>
              </Button>
              <Button variant="outline" className="h-auto py-4 justify-start" onClick={() => handleGenerateQR('thermal')} disabled={qrLoading} data-testid="qr-format-thermal">
                <div className="text-left">
                  <p className="font-semibold">Termic (50x30mm)</p>
                  <p className="text-xs text-slate-500">Pentru imprimante de etichete termice</p>
                </div>
              </Button>
              <Separator className="my-2" />
              <Button variant="outline" className="h-auto py-4 justify-start border-blue-200 hover:bg-blue-50" onClick={handleExportQrZip} disabled={qrLoading} data-testid="qr-format-zip">
                <div className="text-left">
                  <p className="font-semibold text-blue-700">Export JPEG (ZIP)</p>
                  <p className="text-xs text-slate-500">Imagini individuale cu campurile selectate</p>
                </div>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* QR Preview Dialog */}
      <Dialog open={!!showQRPreview} onOpenChange={o => { if (!o) { setShowQRPreview(null); setQrPreviewData(null); } }}>
        <DialogContent className="max-w-sm" data-testid="qr-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="w-5 h-5 text-blue-600" /> Cod QR Echipament
            </DialogTitle>
          </DialogHeader>
          {showQRPreview && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-3 border text-center">
                <p className="font-semibold text-slate-800">{showQRPreview.denumire}</p>
                <p className="text-sm font-mono text-blue-600 mt-0.5">{showQRPreview.code}</p>
                {showQRPreview.marca && <p className="text-xs text-slate-500 mt-0.5">{showQRPreview.marca} | {showQRPreview.serie_utilaj}</p>}
                {showQRPreview.numar_inventar && <p className="text-xs font-semibold text-indigo-700 mt-0.5">Inv: {showQRPreview.numar_inventar}</p>}
              </div>
              {qrPreviewData ? (
                <div className="flex flex-col items-center gap-3">
                  <img
                    src={`data:image/png;base64,${qrPreviewData.qr_base64}`}
                    alt={`QR ${qrPreviewData.code}`}
                    className="w-48 h-48 border rounded-lg"
                    data-testid="qr-preview-image"
                  />
                  <p className="text-xs font-mono text-slate-500 bg-slate-100 px-3 py-1 rounded">{qrPreviewData.qr_content}</p>
                  {qrPreviewData.necesita_service_baterie && (
                    <Badge className="bg-cyan-100 text-cyan-700 border-cyan-200 text-xs">Necesita Service Baterie</Badge>
                  )}
                  <Button variant="outline" size="sm" className="text-xs" onClick={() => {
                    const link = document.createElement('a');
                    link.href = `data:image/png;base64,${qrPreviewData.qr_base64}`;
                    link.download = `qr_${qrPreviewData.code}.png`;
                    link.click();
                  }} data-testid="qr-download-png-btn">
                    <Download className="w-3.5 h-3.5 mr-1" /> Descarca PNG
                  </Button>
                </div>
              ) : (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowQRPreview(null); setQrPreviewData(null); }}>Inchide</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ISCIREquipmentPage;
